

















































































































import { computed, defineComponent, PropType, ref, toRef, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { MessageLinkSubjectTypeEnum } from "@scrile/api-provider/dist/api/MessageLinksProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserAbuseReportType } from "@scrile/api-provider/dist/api/ReportsProvider";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useMessageView from "@/hooks/useMessageView";
import emitter from "@/lib/emitter";
import { isAudioFile } from "@/lib/fileHelpers";
import { $t } from "@/lib/i18nHelpers";
import { getFullPriceData } from "@/lib/moneyHelpers";
import { BlockingStatus } from "@/types";
import { EVENTS, PurchaseType, ROLES } from "@/const";
import AttachmentsInfo from "@/components/AttachmentsInfo";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseMenu from "@/components/BaseMenu";
import LinkifiedText from "@/components/LinkifiedText";
import PaymentConfirmation from "@/components/PaymentConfirmation";
import UnlockForButton from "@/components/UnlockForButton";
import MessageItemAttachment from "./MessageItemAttachment.vue";
import MessageItemLink from "../../components/MessageItemLink";
import config from "@/config";

export default defineComponent({
  name: "MessageItemView",
  components: {
    BaseMenu,
    AttachmentsInfo,
    PaymentConfirmation,
    BaseIcon,
    UnlockForButton,
    MessageItemLink,
    BaseIconButton,
    MessageItemAttachment,
    LinkifiedText,
  },
  props: {
    paymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    userId: { type: String, required: true },
    message: { type: Object as PropType<Message>, required: true },
    blockingStatus: { type: Object as PropType<BlockingStatus>, required: true },
    markAsSeen: { type: Boolean, required: true },
    loading: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { isAuthor, isOpponentMessage, hoursMinutes, fullTime } = useMessageView(toRef(props, "message"));
    const { authorities } = useAuthUser();

    const showUnlock = ref(false);
    const isLikeAnimated = ref(false);

    const isAudioMessage = computed(
      () =>
        !!props.message.attachment[0]?.file &&
        isAudioFile(props.message.attachment[0]?.file) &&
        props.message.attachment.length === 1
    );
    const hasAttachment = computed(() => !!props.message.attachment.length);
    const likeIcon = computed(() => (props.message.likes.count ? "sci-heart" : "sci-heart-outline"));
    const renderLikeIcon = computed(() => isOpponentMessage.value || !!props.message.likes.count);
    const isMessageLiked = computed(() => !!props.message.likes.count);
    const isGeneratedMessage = computed(
      () => props.message.link?.subject.subjectType === MessageLinkSubjectTypeEnum.AI
    );

    const menuItems = computed(() => [
      {
        name: "report",
        text: $t("Report"),
      },
    ]);

    const onReport = () => {
      emitter.emit(EVENTS.OPEN_REPORT_MODAL, {
        subject: {
          subjectId: props.message.id,
          subjectType: UserAbuseReportType.MESSAGE,
        },
        reportedUserId: props.message.senderId,
      });
    };

    const likeAnimationToggle = (messageId: string) => {
      if (messageId !== props.message.id) return;
      isLikeAnimated.value = isMessageLiked.value;
    };

    const onLikeClick = debounce(() => {
      if (isAuthor.value) return;
      emit("toggleLike");
    }, 200);

    const priceData = computed(() =>
      getFullPriceData(props.paymentPackages.find(i => i.id === props.message.attachmentPricePackageId))
    );

    const renderMessageMenu = computed(() => {
      return isOpponentMessage.value
        ? checkAuthorities(authorities.value, ["deleteAnyMessage"])
        : checkAuthorities(authorities.value, ["deleteMyMessage"]) &&
            !props.message.attachmentPurchasers?.includes(props.userId);
    });

    const hasPrice = computed(() => {
      return props.message.attachmentPricePackageId !== "0";
    });

    const isAvailable = computed(() => {
      if (isOpponentMessage.value) {
        return props.message.attachmentAvailable ?? false;
      }
      const purchased = !!props.message.attachmentPurchasers?.find(id => id === props.userId);
      return !hasPrice.value || purchased;
    });

    const showUnlockButton = computed(
      () => hasAttachment.value && !props.message.attachmentAvailable && !props.blockingStatus.authUser
    );

    const linksValidation = computed(() => {
      return (
        !!(props.message?.senderUser as UserPublicData)?.verified ||
        checkRole(props.message?.senderUser?.role || "", ROLES.ADMIN)
      );
    });

    const attachmentClick = async () => {
      if (isAvailable.value || isAuthor.value || props.blockingStatus.authUser) return;
      if (isOpponentMessage && props.message.senderUser?.disabled) {
        emitter.emit(EVENTS.STATUS_SHOW, { text: "This action is forbidden" });
        return;
      }
      showUnlock.value = true;
    };

    watch(toRef(props, "message"), () => {
      likeAnimationToggle(props.message.id);
    });

    return {
      config,
      isAuthor,
      isGeneratedMessage,
      isOpponentMessage,
      isAudioMessage,
      showUnlock,
      showUnlockButton,
      renderMessageMenu,
      isAvailable,
      hasPrice,
      priceData,
      fullTime,
      hoursMinutes,
      linksValidation,
      likeIcon,
      renderLikeIcon,
      isLikeAnimated,
      isMessageLiked,
      hasAttachment,
      PurchaseType,
      menuItems,
      attachmentClick,
      onLikeClick,
      onReport,
    };
  },
});
